import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Trailer,
  TrailerCreateRequest,
  TrailerUpdateRequest,
  TrailerQueryParams,
  TrailersData,
} from '../../types/api/trailer';

/**
 * TrailerService
 *
 * Handles:
 * - Trailer CRUD operations (admin)
 * - Public trailer listing
 */
export class TrailerService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Create trailer with video and thumbnail upload (multipart/form-data)
   * Parts:
   * - data: JSON string (TrailerCreateRequest)
   * - video: File
   * - thumbnail: File
   */
  async createTrailerWithFiles(
    payload: TrailerCreateRequest,
    videoFile: File,
    thumbnailFile: File
  ): Promise<Trailer> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');
    formData.append('video', videoFile, videoFile.name);
    formData.append('thumbnail', thumbnailFile, thumbnailFile.name);

    const response = await this.httpClient.request<Trailer>({
      method: 'POST',
      url: API_ENDPOINTS.TRAILERS.ADMIN_CREATE_MULTIPART,
      data: formData,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create trailer');
    }

    return response.data;
  }

  /**
   * Create trailer using JSON-only endpoint (videoUrl & thumbnailUrl)
   */
  async createTrailerWithJson(payload: TrailerCreateRequest): Promise<Trailer> {
    const response = await this.httpClient.post<Trailer>(
      API_ENDPOINTS.TRAILERS.ADMIN_CREATE_JSON,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create trailer');
    }

    return response.data;
  }

  /**
   * Update trailer with optional new video/thumbnail (multipart/form-data)
   */
  async updateTrailerWithFiles(
    trailerId: string,
    payload: TrailerUpdateRequest,
    videoFile?: File,
    thumbnailFile?: File
  ): Promise<Trailer> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (videoFile) {
      formData.append('video', videoFile, videoFile.name);
    }

    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }

    const response = await this.httpClient.request<Trailer>({
      method: 'PUT',
      url: API_ENDPOINTS.TRAILERS.ADMIN_UPDATE_MULTIPART(trailerId),
      data: formData,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update trailer');
    }

    return response.data;
  }

  /**
   * Update trailer using JSON-only endpoint
   */
  async updateTrailerWithJson(
    trailerId: string,
    payload: TrailerUpdateRequest
  ): Promise<Trailer> {
    const response = await this.httpClient.put<Trailer>(
      API_ENDPOINTS.TRAILERS.ADMIN_UPDATE_JSON(trailerId),
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update trailer');
    }

    return response.data;
  }

  /**
   * Get trailer by ID (admin)
   */
  async getTrailer(trailerId: string): Promise<Trailer> {
    const response = await this.httpClient.get<Trailer>(
      API_ENDPOINTS.TRAILERS.ADMIN_DETAIL(trailerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch trailer');
    }

    return response.data;
  }

  /**
   * List trailers for admin with pagination & filters
   */
  async getTrailers(params: TrailerQueryParams = {}): Promise<TrailersData> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.size !== undefined) queryParams.append('size', String(params.size));
    if (params.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params.isFeatured !== undefined) {
      queryParams.append('isFeatured', String(params.isFeatured));
    }

    const url = `${API_ENDPOINTS.TRAILERS.ADMIN_LIST}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await this.httpClient.get<TrailersData>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch trailers');
    }

    return response.data;
  }

  /**
   * Delete trailer (admin)
   */
  async deleteTrailer(trailerId: string): Promise<void> {
    const response = await this.httpClient.delete(
      API_ENDPOINTS.TRAILERS.ADMIN_DELETE(trailerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete trailer');
    }
  }

  /**
   * Activate trailer (admin)
   */
  async activateTrailer(trailerId: string): Promise<void> {
    const response = await this.httpClient.put(
      API_ENDPOINTS.TRAILERS.ADMIN_ACTIVATE(trailerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to activate trailer');
    }
  }

  /**
   * Deactivate trailer (admin)
   */
  async deactivateTrailer(trailerId: string): Promise<void> {
    const response = await this.httpClient.put(
      API_ENDPOINTS.TRAILERS.ADMIN_DEACTIVATE(trailerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to deactivate trailer');
    }
  }
}

